/**
 * SwipeEditor.jsx
 * Admin content editor for lesson_type === 'swipe'
 *
 * Content shape:
 *   { vocabulary: [{ word, translation, example }] }
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */


import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DEFAULT_ITEM = { word: '', translation: '', example: '' };

export default function SwipeEditor({ value, onChange }) {
  const { t } = useLanguage();
  const vocabulary = Array.isArray(value?.vocabulary) ? value.vocabulary : [];

  const update = (next) => onChange({ ...value, vocabulary: next });

  const addItem = () => update([...vocabulary, { ...DEFAULT_ITEM }]);

  const removeItem = (idx) => update(vocabulary.filter((_, i) => i !== idx));

  const updateItem = (idx, field, val) =>
    update(vocabulary.map((v, i) => (i === idx ? { ...v, [field]: val } : v)));

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...vocabulary];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update(next);
  };

  const moveDown = (idx) => {
    if (idx === vocabulary.length - 1) return;
    const next = [...vocabulary];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update(next);
  };

  return (
    <div className="space-y-3">
      {vocabulary.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {vocabulary.map((item, idx) => (
        <div
          key={idx}
          className="bg-[var(--bg-app)] border-2 border-[var(--border-light)] rounded-xl p-4 space-y-2"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === vocabulary.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                aria-label={t('lessonEditor.swipe.removeItem')}
                className="p-1 rounded hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.swipe.word')} *
              </label>
              <Input
                value={item.word}
                onChange={(e) => updateItem(idx, 'word', e.target.value)}
                placeholder={t('lessonEditor.swipe.wordPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.swipe.translation')} *
              </label>
              <Input
                value={item.translation}
                onChange={(e) => updateItem(idx, 'translation', e.target.value)}
                placeholder={t('lessonEditor.swipe.translationPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.swipe.example')}
              </label>
              <Input
                value={item.example}
                onChange={(e) => updateItem(idx, 'example', e.target.value)}
                placeholder={t('lessonEditor.swipe.examplePlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addItem}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.swipe.addItem')}
      </Button>
    </div>
  );
}
